import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { useMeetingMembers, useMeetingDetail } from 'hooks/useMeeting';
import { MeetingRepository } from 'repository/meetingRepository';
import { useUserInfo } from 'hooks/useAuth';
import { usePaymentList } from 'hooks/usePayment';
import { meetingStore } from 'store/meetingStore';
import { paymentStore } from 'store/paymentStore';
import { PaymentRepository } from 'repository/paymentRepository';
import { useAlert } from 'contexts/AlertContext';
import { calculateSettlement } from 'common/utils/settlementCalculator';
import RouletteSlotModal, { RouletteParticipant } from 'components/payment/RouletteSlotModal';
import { instance } from 'common/axios/axios';
import { getApiUrl } from 'common/axios/getApiUrl';
import { isGuestOf } from 'common/utils/auth';
import {
    IcClose, IcEdit, IcRestaurant, IcBeer, IcCafe, IcTaxi, IcGame, IcShopping,
    IcTicket, IcHotel, IcMoney, IconProps,
} from 'components/common/icons/EmojiIcons';
import {
    AppScreen, Bleed, Button, InverseBanner, Section, SectionHeader,
    SwitchList, SwitchRow, TextField,
} from 'components/ds';
import styles from './CostInput.module.css';

interface Participant { id: number; name: string; kind: 'member' | 'external'; }
type SplitType = 'EQUAL' | 'CUSTOM';
type GroupMember = { leaderId: number; memberIds: number[] };

// emoji 필드에는 카테고리 키 문자열을 저장 (이모지 금지) — 렌더부에서 CATEGORY_ICON 으로 lookup.
// 이름은 렌더 시점에 t() 로 뽑습니다. 모듈 로드 시점엔 i18n 이 아직 준비되지 않아
// i18n.t() 가 빈 문자열을 돌려주고, 그러면 카테고리 이름이 하나도 안 보입니다.
const CATEGORIES = [
    { key: 'meal', emoji: 'meal', labelKey: 'cost.catMeal' },
    { key: 'drink', emoji: 'drink', labelKey: 'cost.catDrink' },
    { key: 'cafe', emoji: 'cafe', labelKey: 'cost.catCafe' },
    { key: 'transport', emoji: 'transport', labelKey: 'cost.catTransport' },
    { key: 'game', emoji: 'game', labelKey: 'cost.catGame' },
    { key: 'shopping', emoji: 'shopping', labelKey: 'cost.catShopping' },
    { key: 'ticket', emoji: 'ticket', labelKey: 'cost.catTicket' },
    { key: 'hotel', emoji: 'hotel', labelKey: 'cost.catHotel' },
    { key: 'other', emoji: 'other', labelKey: 'cost.catOther' },
];

const CATEGORY_ICON: Record<string, React.FC<IconProps>> = {
    meal: IcRestaurant,
    drink: IcBeer,
    cafe: IcCafe,
    transport: IcTaxi,
    game: IcGame,
    shopping: IcShopping,
    ticket: IcTicket,
    hotel: IcHotel,
    other: IcEdit,
    money: IcMoney,
};


const safeDecode = (s?: string | null): string => {
    if (!s) return '';
    try { return decodeURIComponent(s); } catch { return s; }
};

interface CostInputProps {
    /** 모달로 사용할 때 true — 페이지 헤더 / wrapper 없이 폼만 렌더, 저장 후 onClose 호출 */
    inModal?: boolean;
    /** 모달 모드에서 meetingId 강제 지정 — useParams 대체 */
    meetingIdProp?: string;
    /** 모달 모드에서 수정 대상 paymentId 강제 지정 — URL param 대체 */
    paymentIdProp?: string;
    /** 모달 모드에서 placeTag prefill (e.g. 'main' | 'sub:32' | 'etc') */
    placeTagProp?: string;
    /** 모달 닫기 콜백 — 모달 모드에서 헤더 X / 저장 후 자동 닫힘 */
    onClose?: () => void;
    /** 저장 성공 콜백 — 부모에서 새로고침 트리거용 */
    onSaved?: () => void;
}

export const CostInput = (props: CostInputProps = {}) => {
    const { inModal, meetingIdProp, paymentIdProp, placeTagProp, onClose, onSaved } = props;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { meetingId: paramMeetingId } = useParams();
    const meetingId = meetingIdProp || paramMeetingId;
    const { data: membersData } = useMeetingMembers();
    const { data: meetingData } = useMeetingDetail();
    const { data: paymentData } = usePaymentList();
    const { showAlert, showConfirm } = useAlert();
    const { userNo: myUserNo } = useUserInfo(); // JWT 우선 userNo
    // 모임 상세에서 "수정" 진입 시 ?paymentId=X 로 수정 모드 자동 활성화 (모달 모드는 prop 우선)
    const editParamPaymentId = paymentIdProp || (() => {
        try { return new URLSearchParams(window.location.search).get('paymentId'); }
        catch { return null; }
    })();
    // ?placeTag=main|sub:123|etc 로 진입 시 자동 prefill (모임 상세에서 "+ 정산 등록" 버튼이 장소 별로 진입)
    const placeTagParamRaw = placeTagProp || (() => {
        try {
            const sp = new URLSearchParams(window.location.search);
            // 신규: ?placeTag= / 구버전: ?round= (1차/2차/3차/기타) 모두 수용
            return sp.get('placeTag') || sp.get('round') || null;
        } catch { return null; }
    })();

    // 🎰 룰렛
    const [showRoulette, setShowRoulette] = useState(false);

    // 영수증 OCR 로 받은 R2 이미지 URL — 등록 시 payment.receipt_image_url 로 저장
    const receiptImageUrlRef = useRef<string | null>(null);

    // 입력 상태
    const [category, setCategory] = useState('');
    const [customDesc, setCustomDesc] = useState('');
    const [amount, setAmount] = useState('');
    // 장소 태그 — 'main' | `sub:{subPlaceId}` | 'etc' | '' (미선택 = main 으로 저장)
    // ?placeTag= 또는 구버전 ?round=1차/2차/3차/기타 prefill 처리.
    const [placeTag, setPlaceTag] = useState<string>(() => {
        const raw = placeTagParamRaw || '';
        if (raw === 'main' || raw === 'etc' || raw.startsWith('sub:')) return raw;
        // 구버전 라벨 매핑 (sub_places 정보 없을 수 있어 main/etc 만 매핑, 2차/3차 는 mount 후 재적용)
        if (raw === '1차') return 'main';
        if (raw === '기타') return 'etc';
        return '';
    });
    // sub_places — 모임의 추가 장소들 (chip 동적 노출)
    const [subPlaces, setSubPlaces] = useState<Array<{ subPlaceId: number; placeName: string; category: string; sortOrder: number }>>([]);
    // 간단 메모 — 옵셔널
    const [memoText, setMemoText] = useState('');
    const [paidByKey, setPaidByKey] = useState<string>('');
    const [splitKeys, setSplitKeys] = useState<Set<string>>(new Set());
    const [splitType, setSplitType] = useState<SplitType>('EQUAL');
    const [customAmounts, setCustomAmounts] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(false);
    const [toastMsg, setToastMsg] = useState('');

    // 외부 인원 / 묶음
    const [externals, setExternals] = useState<{ externalNo: number; name: string }[]>([]);
    const [newExternalName, setNewExternalName] = useState('');
    const [isAddingExternal, setIsAddingExternal] = useState(false);
    const [groups, setGroups] = useState<GroupMember[]>([]);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [groupLeader, setGroupLeader] = useState<number | ''>('');
    const [groupMembers, setGroupMembers] = useState<number[]>([]);

    // "새 지출 추가" 입력 카드 펼침 상태
    const [isFormExpanded, setIsFormExpanded] = useState(false);

    // CostReceipt 에서 OCR 후 navigate(state) 로 전달된 결과를 form 에 prefill (지출명/금액/영수증 이미지 URL).
    // 한 번 적용 후 state 클리어 — 새로고침/뒤로가기 시 재적용 방지.
    useEffect(() => {
        const ocr = (location.state as any)?.ocr;
        if (!ocr) return;
        if (ocr.storeName) setCustomDesc(String(ocr.storeName));
        if (ocr.totalAmount != null) {
            const n = Number(ocr.totalAmount);
            if (Number.isFinite(n) && n > 0) {
                setAmount(n.toLocaleString('ko-KR'));
            }
        }
        if (ocr.imageUrl) receiptImageUrlRef.current = String(ocr.imageUrl);
        setIsFormExpanded(true);
        navigate(window.location.pathname + window.location.search, { replace: true, state: null });
    }, [location.state]); // eslint-disable-line react-hooks/exhaustive-deps
    // 사용자가 "새 지출 추가" 버튼을 눌러 폼을 열었는지 여부 (초기 자동 펼침 시 autoFocus로 스크롤 점프 방지)
    const [userOpenedForm, setUserOpenedForm] = useState(false);
    // "내가 쏠게요" 모드 — 참여자 분배/금액 불필요
    const [isTreat, setIsTreat] = useState(false);

    // 수정 모달 — null 이면 닫힘
    const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);

    // 페이지 진입 시 스크롤 최상단으로 초기화 (내부 컨테이너 + window 모두)
    const contentRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        // 렌더 직후 + 마이크로태스크 이후 두 번 수행해 autoFocus 스크롤 점프를 덮어씀
        const reset = () => {
            if (contentRef.current) contentRef.current.scrollTop = 0;
            window.scrollTo(0, 0);
        };
        reset();
        requestAnimationFrame(reset);
        setTimeout(reset, 0);
    }, []);

    // "내가 쏜다" 내역 (정산 화면 상단 축하 배너)
    const [treats, setTreats] = useState<any[]>([]);
    useEffect(() => {
        if (!meetingId) return;
        instance
            .get(`${getApiUrl('meetings')}/${meetingId}/payments/treats`)
            .then(res => {
                const list = res?.data?.data || res?.data || [];
                setTreats(Array.isArray(list) ? list : []);
            })
            .catch(() => { /* 무시 */ });
    }, [meetingId]);

    // 데이터 로드 — inModal 모드에서는 부모(MeetingDetail) 가 이미 detail/members/payments 를
    // store 에 보유하므로 중복 fetch 를 skip. 사용자가 지출추가 모달을 열고 취소만 눌러도 5건의
    // 네트워크 + store 갱신이 발생해 부모 화면 재렌더링이 무거워지던 회귀 회피.
    // 외부 멤버 / sub-place 는 CostInput 만 쓰니 mode 와 무관하게 fetch.
    useEffect(() => {
        if (!meetingId) return;
        if (!inModal) {
            meetingStore.onGetMeetingDetail(meetingId);
            meetingStore.onGetMeetingMembers(meetingId);
            paymentStore.onGetPaymentList(meetingId);
        }
        PaymentRepository.onGetExternalMembers(meetingId)
            .then(res => {
                const list = res?.data?.data || res?.data || [];
                setExternals(list.map((e: any) => ({ externalNo: e.externalNo, name: e.name })));
            })
            .catch(() => { /* 없어도 무시 */ });
        MeetingRepository.onGetSubPlaces(meetingId)
            .then((res: any) => {
                const list = res?.data?.data || res?.data || [];
                const filtered = (Array.isArray(list) ? list : [])
                    .filter((sp: any) => sp.category !== 'HOTEL')
                    .map((sp: any) => ({
                        subPlaceId: Number(sp.subPlaceId),
                        placeName: sp.placeName || '',
                        category: sp.category || '',
                        sortOrder: Number(sp.sortOrder || 0),
                    }))
                    .sort((a: any, b: any) => a.sortOrder - b.sortOrder);
                setSubPlaces(filtered);
            })
            .catch(() => setSubPlaces([]));
    }, [meetingId, inModal]);

    // sub_places fetch 완료 후 구버전 ?round=2차/3차 prefill 재적용
    useEffect(() => {
        if (placeTag) return;
        const raw = placeTagParamRaw || '';
        if (raw === '2차' && subPlaces[0]) setPlaceTag(`sub:${subPlaces[0].subPlaceId}`);
        else if (raw === '3차' && subPlaces[1]) setPlaceTag(`sub:${subPlaces[1].subPlaceId}`);
    }, [subPlaces, placeTag, placeTagParamRaw]);

    // 모임 정보 — 화면 상단 배너 + 라운드 라벨에 활용
    const meetingTitle: string = meetingData?.data?.meetingName || '';
    const meetingDateStr: string = useMemo(() => {
        const sched = meetingData?.data?.schedule;
        if (!sched?.startDatetime) return '';
        const d = new Date(sched.startDatetime);
        if (isNaN(d.getTime())) return '';
        const date = d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' });
        const time = d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
        return `${date} ${time}`;
    }, [meetingData]);
    const meetingPlaceName: string = meetingData?.data?.placeName || '';

    const memberParticipants: Participant[] = useMemo(() =>
        membersData?.data?.map((member: any) => ({
            id: member.userNo,
            kind: 'member' as const,
            name: (() => {
                const n = member.nickname || member.nickName || '';
                if (!n) return String(member.userNo);
                return safeDecode(n);
            })()
        })) || [],
        [membersData]
    );

    const externalParticipants: Participant[] = externals.map(e => ({
        id: e.externalNo,
        kind: 'external' as const,
        name: e.name,
    }));

    const participants: Participant[] = [...memberParticipants, ...externalParticipants];

    const keyOf = (p: Participant) => `${p.kind === 'member' ? 'm' : 'e'}:${p.id}`;
    const parseKey = (key: string): { kind: 'member' | 'external'; id: number } | null => {
        const [k, idStr] = key.split(':');
        if (!k || !idStr) return null;
        return { kind: k === 'm' ? 'member' : 'external', id: parseInt(idStr) };
    };

    // 폼 초기값 설정 (멤버 로드 후 한 번) — 결제자 디폴트는 본인(myUserNo).
    // 본인이 멤버 목록에 없는 경우(주최자 게스트 등 예외)에 한해 첫 번째 멤버로 fallback.
    useEffect(() => {
        if (memberParticipants.length > 0 && !paidByKey) {
            const meKey = myUserNo && memberParticipants.some(p => p.id === myUserNo)
                ? `m:${myUserNo}`
                : `m:${memberParticipants[0].id}`;
            setPaidByKey(meKey);
            setSplitKeys(new Set(memberParticipants.map(p => `m:${p.id}`)));
        }
    }, [memberParticipants, paidByKey, myUserNo]);

    // payment list 정규화
    const payments = useMemo(() =>
        (paymentData?.data || []).map((p: any) => ({
            ...p,
            totalAmount: p.totalAmount ?? p.amount ?? p.total_amount ?? 0,
            paidByNickname: p.paidByNickname ? safeDecode(p.paidByNickname) : p.paidByNickname,
            creatorNickname: p.creatorNickname ? safeDecode(p.creatorNickname) : p.creatorNickname,
            updatedByNickname: p.updatedByNickname ? safeDecode(p.updatedByNickname) : p.updatedByNickname,
            splits: (p.splits || []).map((s: any) => ({
                ...s,
                amount: s.amount ?? s.splitAmount ?? 0,
                nickname: s.nickname ? safeDecode(s.nickname) : s.nickname,
            })),
        })),
        [paymentData]
    );

    const settlement = useMemo(() => calculateSettlement(payments), [payments]);
    const { totalAmount, perPersonAmount, balances, transactions, expenses } = settlement;

    // 자동 폼 펼침 제거 — 사용자가 명시적으로 '새 지출 추가' 버튼을 눌러야 폼이 보임

    const catLabelOf = (key: string) => {
        const found = CATEGORIES.find(c => c.key === key);
        return found ? t(found.labelKey) : '';
    };
    const baseDescription = category === 'other' ? customDesc : catLabelOf(category);
    // storeName 인코딩 — payment 테이블 컬럼 추가 없이 장소 태그 + 메모 보관.
    // [main] / [sub:{id}] / [etc] 새 포맷. placeTag 가 빈 값이거나 'main' 이면 prefix 생략.
    const description = useMemo(() => {
        const tag = placeTag === '' || placeTag === 'main' ? '' : `[${placeTag}] `;
        const memo = memoText.trim() ? ` (${memoText.trim()})` : '';
        return `${tag}${baseDescription}${memo}`;
    }, [baseDescription, placeTag, memoText]);
    const amountNumber = parseInt((amount || '0').replace(/,/g, ''));
    const amountValid = amountNumber > 0;
    // 쏘기 모드: description + 결제자만 필요. 아니면 기존대로 금액 + 분배 대상 필요
    // 라운드/메모 prefix·suffix 만 있을 경우 valid 처리되지 않도록 baseDescription 기준 검증.
    const isFormValid = isTreat
        ? (!!baseDescription.trim() && !!paidByKey)
        : (!!baseDescription.trim() && amountValid && splitKeys.size > 0);

    const handleAmountChange = (v: string) => {
        const nums = v.replace(/[^0-9]/g, '');
        setAmount(nums ? parseInt(nums).toLocaleString('ko-KR') : '');
    };

    // 이미 '내가 쏠게요' 결제를 한 멤버 user_no 집합 — 분배 대상 추가 시 매너 컨펌용.
    // (treat 한 사람을 다음 결제 분배에서 빼주는 게 일반적인 매너)
    const treatedPayerIds = useMemo(() => {
        const ids = new Set<number>();
        (payments || []).forEach((p: any) => {
            if (p.isTreat && p.paidByNo != null) ids.add(Number(p.paidByNo));
        });
        return ids;
    }, [payments]);

    const toggleSplit = async (key: string) => {
        const isAdding = !splitKeys.has(key);
        // treat 한 멤버를 분배 대상으로 추가하려는 경우만 컨펌 (해제 시는 패스)
        if (isAdding) {
            const p = participants.find(pp => keyOf(pp) === key);
            if (p && p.kind === 'member' && treatedPayerIds.has(p.id)) {
                const ok = await showConfirm({
                    title: t('cost.alreadyTreatedOne', { name: p.name }),
                    message: t('cost.distributeTodayConfirm'),
                    confirmText: t('cost.include'),
                });
                if (!ok) return;
            }
        }
        setSplitKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const selectAllSplit = async () => {
        // 전체 선택 시에도 treat 한 멤버 한 명씩 컨펌 (단, 모두 한 번에 묻지 않고 통합 한 번 컨펌)
        const treatNames = participants
            .filter(p => p.kind === 'member' && treatedPayerIds.has(p.id))
            .map(p => p.name);
        if (treatNames.length > 0) {
            const ok = await showConfirm({
                title: t('cost.alreadyTreatedMany', { names: treatNames.join(', ') }),
                message: t('cost.selectAllConfirm'),
                confirmText: t('cost.selectAllBtn'),
            });
            if (!ok) return;
        }
        setSplitKeys(new Set(participants.map(keyOf)));
    };

    const getGroupLeaderFor = (userId: number): Participant | null => {
        const group = groups.find(g => g.memberIds.includes(userId));
        if (!group) return null;
        return memberParticipants.find(p => p.id === group.leaderId) || null;
    };

    const handleAddExternal = async () => {
        if (!meetingId || !newExternalName.trim() || isAddingExternal) return;
        setIsAddingExternal(true);
        try {
            const res = await PaymentRepository.onPostCreateExternalMember(meetingId, newExternalName.trim());
            const created = res?.data?.data || res?.data;
            if (created?.externalNo) {
                const newItem = { externalNo: created.externalNo, name: created.name };
                setExternals(prev => [...prev, newItem]);
                setSplitKeys(prev => {
                    const next = new Set(prev);
                    next.add(`e:${newItem.externalNo}`);
                    return next;
                });
                setNewExternalName('');
            }
        } catch {
            showAlert({ message: t('cost.externalAddFail') });
        } finally {
            setIsAddingExternal(false);
        }
    };

    // 폼 → API payload 변환
    const buildPayload = () => {
        let splitAmong: number[] = [];
        let splitAmongExternals: number[] = [];
        splitKeys.forEach(key => {
            const parsed = parseKey(key);
            if (!parsed) return;
            if (parsed.kind === 'member') splitAmong.push(parsed.id);
            else splitAmongExternals.push(parsed.id);
        });
        groups.forEach(g => {
            g.memberIds.forEach(memberId => {
                if (splitAmong.includes(memberId)) {
                    splitAmong = splitAmong.filter(id => id !== memberId);
                    if (!splitAmong.includes(g.leaderId)) splitAmong.push(g.leaderId);
                }
            });
        });
        const paidParsed = parseKey(paidByKey);
        const paidByNo = paidParsed?.kind === 'member' ? paidParsed.id : undefined;
        const paidByExternalNo = paidParsed?.kind === 'external' ? paidParsed.id : undefined;
        const payload: any = {
            meetingId,
            storeName: description.trim(),
            totalAmount: isTreat ? 0 : amountNumber,
            splitType,
            paidByNo,
            paidByExternalNo,
            splitAmong: isTreat ? [] : splitAmong,
            splitAmongExternals: isTreat ? [] : splitAmongExternals,
            isTreat,
        };
        if (receiptImageUrlRef.current) {
            payload.receiptImageUrl = receiptImageUrlRef.current;
        }
        if (splitType === 'CUSTOM') {
            const customAmountsMap: Record<number, number> = {};
            const customExternalAmountsMap: Record<number, number> = {};
            splitAmong.forEach(id => {
                customAmountsMap[id] = parseInt((customAmounts[`m:${id}`] || '0').replace(/,/g, ''));
            });
            splitAmongExternals.forEach(id => {
                customExternalAmountsMap[id] = parseInt((customAmounts[`e:${id}`] || '0').replace(/,/g, ''));
            });
            payload.customAmounts = customAmountsMap;
            payload.customExternalAmounts = customExternalAmountsMap;
        }
        return payload;
    };

    const resetForm = useCallback(() => {
        setCategory('');
        setCustomDesc('');
        setAmount('');
        setPlaceTag('');
        setMemoText('');
        receiptImageUrlRef.current = null;
        if (memberParticipants.length > 0) {
            // 등록/취소 후에도 결제자 디폴트는 본인.
            const meKey = myUserNo && memberParticipants.some(p => p.id === myUserNo)
                ? `m:${myUserNo}`
                : `m:${memberParticipants[0].id}`;
            setPaidByKey(meKey);
            setSplitKeys(new Set(memberParticipants.map(p => `m:${p.id}`)));
        }
        setSplitType('EQUAL');
        setCustomAmounts({});
        setEditingPaymentId(null);
        setIsTreat(false);
    }, [memberParticipants, myUserNo]);

    const showToast = (msg: string) => {
        setToastMsg(msg);
        setTimeout(() => setToastMsg(''), 2200);
    };

    const handleSubmit = async () => {
        if (!isFormValid || !meetingId || isLoading) return;
        setIsLoading(true);
        try {
            const payload = buildPayload();
            console.log('[CostInput] submit payload:', payload);
            let response;
            if (editingPaymentId) {
                response = await PaymentRepository.onPutUpdatePayment(editingPaymentId, payload);
            } else {
                response = await PaymentRepository.onPostCreatePayment(meetingId, payload);
            }
            const ok = response?.status >= 200 && response?.status < 300;
            if (ok) {
                const wasEdit = !!editingPaymentId;
                resetForm();
                paymentStore.onGetPaymentList(meetingId);
                setEditingPaymentId(null);
                setIsFormExpanded(false);
                onSaved?.();
                if (inModal) {
                    // 모달 모드 — 토스트 알림 후 닫기
                    await showAlert({ message: wasEdit ? t('cost.expenseEdited') : t('cost.expenseRegistered') });
                    onClose?.();
                } else {
                    showToast(wasEdit ? t('cost.expenseEditedToast') : t('cost.expenseRegistered'));
                    // 단건 처리 후 모임 상세의 정산 섹션으로 자동 복귀 — 분배/송금 결과는 거기서 확인
                    navigate(`/meeting/detail/${meetingId}`, { state: { scrollToSettlement: true } });
                }
            } else {
                console.error('[CostInput] submit failed:', response?.status, response?.data);
                const serverMsg = response?.data?.message || response?.data?.error || `상태 ${response?.status ?? '?'}`;
                showAlert({ message: `${editingPaymentId ? t('cost.editFailLabel') : t('cost.addFailLabel')} — ${serverMsg}` });
            }
        } catch (e: any) {
            console.error('[CostInput] submit exception:', e);
            const detail = e?.response?.data?.message || e?.message || t('cost.errorOccurred');
            showAlert({ message: `${t('cost.errorOccurred')} (${detail})` });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (paymentId: string) => {
        const confirmed = await showConfirm({ message: t('cost.deleteConfirm') });
        if (!confirmed || !meetingId) return;
        try {
            const res = await PaymentRepository.onDeletePayment(paymentId);
            const ok = res?.status >= 200 && res?.status < 300;
            if (ok) {
                paymentStore.onGetPaymentList(meetingId);
                showToast('삭제되었습니다');
                setEditingPaymentId(null);
            } else {
                showAlert({ message: res?.data?.message || t('cost.deleteFailMsg') });
            }
        } catch {
            showAlert({ message: t('cost.deleteError') });
        }
    };

    // 수정 모드 진입 — 기존 지출 데이터를 폼에 로드
    const startEdit = (payment: any) => {
        // 장소 태그 / 메모 디코드 — storeName 형식:
        //  · 신규: "[main] / [sub:123] / [etc] 식사비 (메모)"
        //  · 구버전: "[1차] / [2차] / [3차] / [기타] 식사비 (메모)"
        const rawStoreName: string = payment.storeName || '';
        let work = rawStoreName;
        let extractedTag = 'main';
        const tagMatch = work.match(/^\[([^\]]+)\]\s*(.*)$/);
        if (tagMatch) {
            const t = tagMatch[1];
            work = tagMatch[2];
            if (t === 'main' || t === 'etc' || t.startsWith('sub:')) {
                extractedTag = t;
            } else if (t === '1차') extractedTag = 'main';
            else if (t === '기타') extractedTag = 'etc';
            else if (t === '2차' && subPlaces[0]) extractedTag = `sub:${subPlaces[0].subPlaceId}`;
            else if (t === '3차' && subPlaces[1]) extractedTag = `sub:${subPlaces[1].subPlaceId}`;
            else extractedTag = 'etc';
        }
        let extractedMemo = '';
        const memoMatch = work.match(/^(.*?)\s*\(([^()]+)\)\s*$/);
        if (memoMatch) {
            work = memoMatch[1];
            extractedMemo = memoMatch[2];
        }
        setPlaceTag(extractedTag);
        setMemoText(extractedMemo);
        const cleanStoreName = work.trim();
        // 카테고리 추출 (cleanStoreName 의 시작이 카테고리 라벨이면 매칭)
        const matchedCat = CATEGORIES.find(c => cleanStoreName.startsWith(t(c.labelKey)));
        if (matchedCat) {
            setCategory(matchedCat.key);
            setCustomDesc(matchedCat.key === 'other' ? cleanStoreName : '');
        } else {
            setCategory('other');
            setCustomDesc(cleanStoreName);
        }
        setAmount(Number(payment.totalAmount || 0).toLocaleString('ko-KR'));
        // 결제자
        if (payment.paidByExternalNo) {
            setPaidByKey(`e:${payment.paidByExternalNo}`);
        } else if (payment.paidByNo) {
            setPaidByKey(`m:${payment.paidByNo}`);
        }
        // 분할 대상
        const sk = new Set<string>();
        (payment.splits || []).forEach((s: any) => {
            if (s.userNo) sk.add(`m:${s.userNo}`);
            else if (s.externalNo) sk.add(`e:${s.externalNo}`);
        });
        setSplitKeys(sk);
        setSplitType((payment.splitType as SplitType) || 'EQUAL');
        // CUSTOM 금액 설정
        if (payment.splitType === 'CUSTOM') {
            const ca: Record<string, string> = {};
            (payment.splits || []).forEach((s: any) => {
                const k = s.userNo ? `m:${s.userNo}` : `e:${s.externalNo}`;
                ca[k] = Number(s.amount || 0).toLocaleString('ko-KR');
            });
            setCustomAmounts(ca);
        } else {
            setCustomAmounts({});
        }
        setEditingPaymentId(payment.paymentId);
    };

    const cancelEdit = () => {
        resetForm();
        setEditingPaymentId(null);
    };

    // ?paymentId=X 로 진입한 경우 — payments 데이터 ready 되면 자동으로 수정 모드
    useEffect(() => {
        if (!editParamPaymentId || editingPaymentId) return;
        if (!payments || payments.length === 0) return;
        const target = payments.find((p: any) => String(p.paymentId) === String(editParamPaymentId));
        if (target) {
            startEdit(target);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payments, editParamPaymentId]);

    const handleAddGroup = () => {
        if (!groupLeader || groupMembers.length === 0) return;
        setGroups(prev => [...prev, { leaderId: Number(groupLeader), memberIds: groupMembers }]);
        setGroupLeader('');
        setGroupMembers([]);
        setShowGroupModal(false);
        showToast('묶음이 설정되었습니다');
    };

    const removeGroup = (idx: number) => {
        setGroups(prev => prev.filter((_, i) => i !== idx));
    };

    // 폼 본문 렌더 (추가 카드와 수정 모달 양쪽에서 재사용)
    /** 어디서 썼는지 고르는 칩 하나 */
    const PlaceChip = ({ tag, prefix, name }: { tag: string; prefix?: string; name: string }) => {
        const on = placeTag === tag;
        return (
            <button
                type="button"
                className={on ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                onClick={() => setPlaceTag(prev => (prev === tag ? '' : tag))}
                aria-pressed={on}
            >
                {prefix ? <span className={styles.chipPrefix}>{prefix}</span> : null}
                <span className={styles.chipName}>{name}</span>
            </button>
        );
    };

    const renderForm = (mode: 'add' | 'edit') => (
        <>
            {/* 1 — 무엇에 썼는지 */}
            <Section spacing="tight">
                <SectionHeader title={t('cost.step1Label')} />
                <div className={styles.catGrid}>
                    {CATEGORIES.map(cat => {
                        const Ic = CATEGORY_ICON[cat.emoji] || IcMoney;
                        const on = category === cat.key;
                        return (
                            <button
                                key={cat.key}
                                type="button"
                                className={on ? `${styles.cat} ${styles.catOn}` : styles.cat}
                                onClick={() => setCategory(cat.key)}
                                aria-pressed={on}
                            >
                                <span className={styles.catIcon}><Ic size={20} /></span>
                                <span className={styles.catLabel}>{t(cat.labelKey)}</span>
                            </button>
                        );
                    })}
                </div>
                {category === 'other' && (
                    <div className={styles.field}>
                        <TextField
                            id="cost-custom-desc"
                            label={t('cost.step1Label')}
                            type="text"
                            placeholder={t('cost.customItemPlaceholder')}
                            value={customDesc}
                            onChange={e => setCustomDesc(e.target.value)}
                            maxLength={20}
                        />
                    </div>
                )}
            </Section>

            {/* 내가 쏜다 — 본인만 켤 수 있습니다 */}
            {category && (() => {
                const myMemberKey = myUserNo ? `m:${myUserNo}` : '';
                const canTreat = !!myUserNo && memberParticipants.some(p => `m:${p.id}` === myMemberKey);
                return (
                    <Section divided>
                        <SwitchList>
                            <SwitchRow
                                label={isTreat ? t('cost.treatOn') : t('cost.treatOff')}
                                desc={isTreat ? t('cost.treatOnSub') : t('cost.treatOffSub')}
                                checked={isTreat}
                                disabled={!canTreat}
                                onChange={(next) => {
                                    setIsTreat(next);
                                    // 켜면 결제자를 나로 고정 — "내가" 쏘는 것이므로
                                    if (next) setPaidByKey(myMemberKey);
                                }}
                            />
                        </SwitchList>
                        {!canTreat && <p className={styles.hint}>{t('cost.treatOnlyMember')}</p>}
                    </Section>
                );
            })()}

            {/* 2 — 누가 냈고 얼마인지 */}
            {category && (
                <Section divided>
                    <SectionHeader title={t('cost.step2Label')} />
                    {participants.length === 0 ? (
                        <p className={styles.hint}>{t('cost.noParticipants')}</p>
                    ) : (
                        <div className={styles.people}>
                            {participants.map(p => {
                                const k = keyOf(p);
                                const selected = paidByKey === k;
                                const isMe = myUserNo && p.kind === 'member' && p.id === myUserNo;
                                // 내가 쏠 때는 나만 결제자가 될 수 있습니다
                                const locked = isTreat && !isMe;
                                return (
                                    <button
                                        key={k}
                                        type="button"
                                        className={selected ? `${styles.person} ${styles.personOn}` : styles.person}
                                        onClick={() => { if (!locked) setPaidByKey(k); }}
                                        disabled={locked}
                                        aria-pressed={selected}
                                    >
                                        <span className={styles.personAvatar}>{(p.name || '?').charAt(0)}</span>
                                        <span className={styles.personName}>{p.name}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {!isTreat ? (
                        <div className={styles.amountRow}>
                            <input
                                id={`cost-amount-${mode}`}
                                className={styles.amount}
                                placeholder={t('cost.amountPlaceholder')}
                                value={amount}
                                onChange={e => handleAmountChange(e.target.value)}
                                inputMode="numeric"
                                autoFocus={mode === 'add' && userOpenedForm}
                                aria-label={t('cost.amountPlaceholder')}
                            />
                            <span className={styles.amountUnit}>{t('cost.amountUnit')}</span>
                        </div>
                    ) : (
                        <p className={styles.hint}>{t('cost.treatNotice')}</p>
                    )}
                </Section>
            )}

            {/* 3 — 누구와 나눌지 */}
            {category && !isTreat && amountValid && (
                <Section divided>
                    <SectionHeader
                        title={t('cost.step3Label')}
                        count={splitKeys.size || undefined}
                        moreLabel={t('cost.selectAll')}
                        onMore={selectAllSplit}
                    />
                    <div className={styles.chips}>
                        {([
                            { v: 'EQUAL' as const, label: t('cost.equalSplit') },
                            { v: 'CUSTOM' as const, label: t('cost.customSplit') },
                        ]).map(o => (
                            <button
                                key={o.v}
                                type="button"
                                className={splitType === o.v ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                                onClick={() => setSplitType(o.v)}
                                aria-pressed={splitType === o.v}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>

                    <ul className={styles.splitList}>
                        {participants.map(p => {
                            const k = keyOf(p);
                            const isSelected = splitKeys.has(k);
                            const leader = p.kind === 'member' ? getGroupLeaderFor(p.id) : null;
                            return (
                                <li key={k} className={styles.splitItem}>
                                    <button
                                        type="button"
                                        className={isSelected ? `${styles.splitPick} ${styles.splitPickOn}` : styles.splitPick}
                                        onClick={() => toggleSplit(k)}
                                        aria-pressed={isSelected}
                                    >
                                        <span className={styles.check} aria-hidden />
                                        <span className={styles.splitName}>
                                            {p.name}
                                            {p.kind === 'external' && (
                                                <span className={styles.splitTag}>{t('cost.externalTag')}</span>
                                            )}
                                            {leader && <span className={styles.splitTag}>→ {leader.name}</span>}
                                        </span>
                                    </button>

                                    {splitType === 'EQUAL' && isSelected && amountValid && (
                                        <span className={styles.splitAmount}>
                                            {Math.round(amountNumber / splitKeys.size).toLocaleString()}
                                            {t('cost.amountUnit')}
                                        </span>
                                    )}
                                    {splitType === 'CUSTOM' && isSelected && (
                                        <input
                                            className={styles.splitInput}
                                            placeholder="0"
                                            value={customAmounts[k] || ''}
                                            onChange={e => {
                                                const nums = e.target.value.replace(/[^0-9]/g, '');
                                                setCustomAmounts(prev => ({
                                                    ...prev,
                                                    [k]: nums ? parseInt(nums).toLocaleString('ko-KR') : '',
                                                }));
                                            }}
                                            inputMode="numeric"
                                            aria-label={p.name}
                                        />
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    {/* 모임 밖 사람 추가 */}
                    <div className={styles.externalAdd}>
                        <span className={styles.subLabel}>{t('cost.externalAddLabel')}</span>
                        <div className={styles.externalRow}>
                            <input
                                className={styles.externalInput}
                                placeholder={t('cost.externalNamePlaceholder')}
                                value={newExternalName}
                                onChange={e => setNewExternalName(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleAddExternal(); }}
                                maxLength={30}
                                aria-label={t('cost.externalAddLabel')}
                            />
                            <Button
                                variant="secondary"
                                size="md"
                                onClick={handleAddExternal}
                                disabled={!newExternalName.trim() || isAddingExternal}
                            >
                                {t('cost.addBtn')}
                            </Button>
                        </div>
                    </div>

                    {/* 묶어서 내는 사람 */}
                    {groups.length > 0 && (
                        <ul className={styles.groupList}>
                            {groups.map((g, idx) => {
                                const leader = participants.find(p => p.id === g.leaderId);
                                const members = g.memberIds
                                    .map(id => participants.find(p => p.id === id)?.name)
                                    .filter(Boolean);
                                return (
                                    <li key={idx} className={styles.groupItem}>
                                        <span className={styles.groupText}>
                                            {members.join(', ')} → {leader?.name}
                                        </span>
                                        <button
                                            type="button"
                                            className={styles.groupRemove}
                                            onClick={() => removeGroup(idx)}
                                            aria-label={t('cost.delete')}
                                        >
                                            <IcClose size={14} />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <div className={styles.groupAdd}>
                        <Button variant="ghost" size="md" block onClick={() => setShowGroupModal(true)}>
                            {t('cost.groupBtn')}
                        </Button>
                    </div>
                </Section>
            )}

            {/* 어디서 · 메모 (선택) */}
            {category && (
                <Section divided>
                    <SectionHeader title={t('cost.placeMemoLabel')} sub={t('cost.optional')} />

                    <span className={styles.subLabel}>{t('cost.whereSpent')}</span>
                    <div className={styles.chips}>
                        <PlaceChip
                            tag="main"
                            prefix={t('category.primary')}
                            name={meetingPlaceName || t('cost.mainPlace')}
                        />
                        {subPlaces.map((sp, idx) => (
                            <PlaceChip
                                key={sp.subPlaceId}
                                tag={`sub:${sp.subPlaceId}`}
                                prefix={t('cost.nthPlace', { n: idx + 2 })}
                                name={sp.placeName || t('cost.placeNameUnset')}
                            />
                        ))}
                        <PlaceChip tag="etc" name={t('cost.etcPlace')} />
                    </div>

                    <div className={styles.field}>
                        <TextField
                            id={`cost-memo-${mode}`}
                            label={t('cost.simpleMemo')}
                            type="text"
                            placeholder={t('cost.memoPlaceholderEx')}
                            value={memoText}
                            onChange={e => setMemoText(e.target.value)}
                            maxLength={60}
                        />
                    </div>
                </Section>
            )}

            {/* 영수증으로 채우기 — 게스트는 업로드·인식 서버(wiz-common·wizai)가 게스트를 몰라서 막히므로 숨김 */}
            {!editingPaymentId && !isGuestOf(meetingId) && (
                <Section divided>
                    <Button
                        variant="secondary"
                        size="lg"
                        block
                        onClick={() => navigate(`/cost/receipt/${meetingId}`)}
                    >
                        {t('cost.receiptBtn')}
                    </Button>
                </Section>
            )}

            {/* 저장 */}
            <Section divided last>
                <div className={styles.actions}>
                    <Button
                        variant="primary"
                        size="lg"
                        block
                        onClick={(e) => { e.stopPropagation(); handleSubmit(); }}
                        disabled={!isFormValid || isLoading}
                    >
                        {isLoading
                            ? (mode === 'edit' ? t('cost.saveProgress') : t('cost.addProgress'))
                            : mode === 'edit'
                                ? t('cost.editSave')
                                : t('cost.addFormat', {
                                    desc: description || t('cost.defaultDesc'),
                                    amount: amountValid ? amountNumber.toLocaleString() + t('cost.amountUnit') : '',
                                })}
                    </Button>

                    {mode !== 'edit' && amountValid && splitKeys.size >= 2 && (
                        <Button
                            variant="secondary"
                            size="lg"
                            block
                            onClick={() => setShowRoulette(true)}
                            disabled={isLoading}
                        >
                            {t('cost.rouletteBtn')}
                        </Button>
                    )}

                    {mode === 'edit' && (
                        <Button
                            variant="danger"
                            size="lg"
                            block
                            onClick={(e) => { e.stopPropagation(); if (editingPaymentId) handleDelete(editingPaymentId); }}
                        >
                            {t('cost.delete')}
                        </Button>
                    )}

                    {((mode === 'add' && payments.length > 0) || mode === 'edit') && (
                        <Button
                            variant="ghost"
                            size="lg"
                            block
                            onClick={(e) => {
                                e.stopPropagation();
                                if (mode === 'edit') cancelEdit();
                                else { resetForm(); setIsFormExpanded(false); }
                                if (inModal) onClose?.();
                            }}
                        >
                            {t('cost.cancel')}
                        </Button>
                    )}
                </div>
            </Section>
        </>
    );

    /* 모임 상세 안에서 겹쳐 뜰 때 — 화면 껍데기 없이 폼만 */
    if (inModal) {
        return (
            <div
                className={styles.overlay}
                role="dialog"
                aria-modal="true"
                aria-label={editingPaymentId ? t('cost.editExpense') : t('cost.newExpense')}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.sheetHead}>
                        <h2 className={styles.sheetTitle}>
                            {editingPaymentId ? t('cost.editExpense') : t('cost.newExpense')}
                        </h2>
                        <button
                            type="button"
                            className={styles.sheetClose}
                            onClick={(e) => { e.stopPropagation(); onClose?.(); }}
                            aria-label={t('common.close')}
                        >
                            <IcClose size={20} />
                        </button>
                    </div>
                    <div className={styles.sheetBody} ref={contentRef}>
                        {renderForm(editingPaymentId ? 'edit' : 'add')}
                    </div>
                    {toastMsg && <div className={styles.toast}>{toastMsg}</div>}
                </div>
            </div>
        );
    }

    return (
        <AppScreen title={t('cost.headerTitle')} bar="sub">
            {/* 어떤 모임의 정산인지 */}
            {meetingTitle && (
                <button
                    type="button"
                    className={styles.meetingRow}
                    onClick={() => meetingId && navigate(`/meeting/detail/${meetingId}`)}
                >
                    <span className={styles.meetingBody}>
                        <span className={styles.meetingLabel}>{t('cost.settlingMeeting')}</span>
                        <span className={styles.meetingName}>{meetingTitle}</span>
                        {(meetingDateStr || meetingPlaceName) && (
                            <span className={styles.meetingMeta}>
                                {meetingDateStr}{meetingPlaceName ? ` · ${meetingPlaceName}` : ''}
                            </span>
                        )}
                    </span>
                </button>
            )}

            {/* 내가 쏜다 축하 */}
            {treats.length > 0 && (
                <Section spacing="tight">
                    <Bleed>
                        <InverseBanner
                            title={t('home.treatTitle', {
                                who: treats[0].paidByNickname || t('cost.someone'),
                            })}
                            sub={treats[0].storeName}
                        />
                    </Bleed>
                </Section>
            )}

            <div ref={contentRef}>
                {renderForm(editingPaymentId ? 'edit' : 'add')}
            </div>

            {/* 묶어서 내기 */}
            {showGroupModal && (
                <div className={styles.overlay} onClick={() => setShowGroupModal(false)}>
                    <div
                        className={styles.sheet}
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('cost.groupModalTitle')}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className={styles.sheetHead}>
                            <h2 className={styles.sheetTitle}>{t('cost.groupModalTitle')}</h2>
                            <button
                                type="button"
                                className={styles.sheetClose}
                                onClick={() => setShowGroupModal(false)}
                                aria-label={t('common.close')}
                            >
                                <IcClose size={20} />
                            </button>
                        </div>
                        <div className={styles.sheetBody}>
                            <span className={styles.subLabel}>{t('cost.groupLeaderLabel')}</span>
                            <div className={styles.chips}>
                                {participants.map(p => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        className={groupLeader === p.id ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                                        onClick={() => setGroupLeader(p.id)}
                                        aria-pressed={groupLeader === p.id}
                                    >
                                        {p.name}
                                    </button>
                                ))}
                            </div>

                            <span className={styles.subLabel}>{t('cost.groupMembersLabel')}</span>
                            <div className={styles.chips}>
                                {participants.filter(p => p.id !== groupLeader).map(p => {
                                    const on = groupMembers.includes(p.id);
                                    return (
                                        <button
                                            key={p.id}
                                            type="button"
                                            className={on ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                                            onClick={() => setGroupMembers(prev =>
                                                prev.includes(p.id) ? prev.filter(id => id !== p.id) : [...prev, p.id])}
                                            aria-pressed={on}
                                        >
                                            {p.name}
                                        </button>
                                    );
                                })}
                            </div>

                            {groupLeader && groupMembers.length > 0 && (
                                <p className={styles.hint}>
                                    {t('cost.groupPreview', {
                                        members: groupMembers
                                            .map(id => participants.find(p => p.id === id)?.name)
                                            .filter(Boolean).join(', '),
                                        leader: participants.find(p => p.id === groupLeader)?.name,
                                    })}
                                </p>
                            )}

                            <div className={styles.sheetActions}>
                                <Button
                                    variant="primary"
                                    size="lg"
                                    block
                                    onClick={handleAddGroup}
                                    disabled={!groupLeader || groupMembers.length === 0}
                                >
                                    {t('cost.groupConfirm')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {toastMsg && <div className={styles.toast}>{toastMsg}</div>}

            <RouletteSlotModal
                open={showRoulette}
                meetingId={meetingId!}
                storeName={baseDescription || t('cost.defaultDesc')}
                totalAmount={amountNumber}
                participants={Array.from(splitKeys).map(k => {
                    const parsed = parseKey(k);
                    if (!parsed || parsed.kind !== 'member') return null;
                    const p = participants.find(x => x.kind === 'member' && x.id === parsed.id);
                    return p ? { userNo: p.id, nickname: p.name } as RouletteParticipant : null;
                }).filter(Boolean) as RouletteParticipant[]}
                currentUserNo={myUserNo || Number(sessionStorage.getItem('userNo')) || 0}
                onClose={() => setShowRoulette(false)}
                onComplete={() => {
                    setShowRoulette(false);
                    if (meetingId) {
                        paymentStore.onGetPaymentList(meetingId);
                        setToastMsg(t('cost.rouletteReflect'));
                        setTimeout(() => setToastMsg(''), 3500);
                    }
                    setAmount('');
                    setCustomDesc('');
                    setCategory('');
                    setSplitKeys(new Set());
                }}
            />
        </AppScreen>
    );
};
